import { useMemo, useCallback, FC } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from "chart.js";
import { Button } from "./ui/button";
import { Typography } from "./ui";
import PerformanceDisplay from "./ui/performance-display";
import NavigationHeader from "./ui/navigation-header";
import MetricDisplay from "./ui/metric-display";
import dayjs from "dayjs";
import CurrencyFormatter from "../services/currencyFormatterService";
import { useNavigate } from "react-router-dom";
import { useAssets } from "../hooks/useAssets";
import { Asset } from "../types/asset";
import AutoStackPerformanceChartSkeleton from "./skeletons/AutoStackPerformanceChartSkeleton";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler
);

interface PerformanceDataPoint {
  accumulatedAsset: number;
  fiatValue: number;
  timestamp: string;
}

interface AutoStackStats {
  assetBalance: number;
  totalStacked: number;
  totalAccumulated: number;
  avgBuyPrice: number;
  totalBalance: number;
  performance: PerformanceDataPoint[];
  labels: string[];
  data?: {
    totalStacked: number;
    totalBalance: number;
    performance: PerformanceDataPoint[];
  };
}

interface SingleAutoStackData {
  _id: string;
  asset: Asset;
  name: string;
  amount: number;
  frequency: string;
  asset_balance: number;
  fiat_balance: number;
  status: string;
  funding_type: string;
  reference: string;
  bank_transfer_reference: string;
  start_date: string;
  createdAt: string;
  updatedAt: string;
  schedule: {
    dayOfWeek: string;
  };
  next_purchase?: {
    scheduled_purchase_date: string;
    amount_fiat: number;
    status: string;
  };
  purchases: Array<{
    scheduled_purchase_date: string;
    amount_fiat: number;
    status: string;
    createdAt: string;
  }>;
}

interface AutoStackPerformanceChartProps {
  autoStackStats?: AutoStackStats;
  isLoading?: boolean;
  chartHeight?: string;
  singleAutoStack?: SingleAutoStackData | boolean;
  overallAutoStack?: boolean;
}

const AutoStackPerformanceChart: FC<AutoStackPerformanceChartProps> = ({
  autoStackStats,
  isLoading = false,
  chartHeight = "h-[164px] autostack-performance-chart",
  singleAutoStack = false,
  overallAutoStack = false,
}) => {
  const navigate = useNavigate();
  const currencyFormatter = new CurrencyFormatter({ locale: "en-ZA" });
  const { assets } = useAssets();

  const singleAutoStackMetrics = useMemo(() => {
    if (!singleAutoStack || typeof singleAutoStack === "boolean") return null;

    const stackData = singleAutoStack as SingleAutoStackData;

    const totalStacked = autoStackStats?.totalStacked ?? 0;
    const totalAccumulated = autoStackStats?.totalAccumulated ?? 0;
    const assetBalance = autoStackStats?.assetBalance ?? 0;
    const averageBuyPrice = autoStackStats?.avgBuyPrice ?? 0;

    const currentValue = stackData.fiat_balance || 0;

    const performanceDifference = currentValue - totalStacked;
    const performancePercentage =
      totalStacked > 0 ? (performanceDifference / totalStacked) * 100 : 0;

    return {
      totalStacked,
      totalAccumulated,
      assetBalance,
      averageBuyPrice,
      currentValue,
      performanceDifference,
      performancePercentage,
      assetName: stackData.asset.asset.toUpperCase() || "CRYPTO",
      stackName: stackData?.name || "My Stack",
      image: assets.find((a) => a.asset === stackData.asset.asset)?.image || "",
      stackId: stackData?._id || "",
    };
  }, [assets, singleAutoStack, autoStackStats]);

  const { performanceData = [] } = useMemo(
    () => ({
      labels: autoStackStats?.labels ?? [],
      performanceData:
        autoStackStats?.data?.performance ?? autoStackStats?.performance ?? [],
    }),
    [autoStackStats]
  );

  const dateLabelFormat = useMemo(() => {
    const dates = performanceData.map((d) => dayjs(d.timestamp));
    if (dates.length === 0) return [];

    const first = dates[0];
    const last = dates[dates.length - 1];
    const daysDiff = last.diff(first, "day");

    const format =
      daysDiff <= 7
        ? "ddd"
        : daysDiff <= 60
          ? "DD/MM"
          : first.year() !== last.year()
            ? "MMM YY"
            : "MMM";
    return format;
  }, [performanceData]);

  const chartData = useMemo(
    () => ({
      labels: performanceData.map((item) => item.timestamp),
      datasets: [
        {
          label: "Performance",
          data: performanceData.map((point) => point.fiatValue),
          fill: true,
          borderColor: "#57249C",
          backgroundColor: (context: any) => {
            const { chart } = context;
            const { ctx, chartArea } = chart;

            if (!chartArea) return null;

            const gradient = ctx.createLinearGradient(
              0,
              chartArea.top,
              0,
              chartArea.bottom
            );
            gradient.addColorStop(0, "rgba(87, 36, 156, 0.50)");
            gradient.addColorStop(1, "rgba(255, 255, 255, 0.00)");
            return gradient;
          },
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 6,
          pointBackgroundColor: "#57249C",
          pointBorderColor: "#ffffff",
          pointBorderWidth: 2,
          pointHitRadius: 10,
        },
      ],
    }),
    [performanceData]
  );

  const performanceStats = useMemo(() => {
    if (!performanceData.length) return null;

    const totalBalance =
      autoStackStats?.data?.totalBalance ?? autoStackStats?.totalBalance ?? 0;
    const totalStacked = autoStackStats?.totalStacked ?? 0;

    const profitLoss = totalBalance - totalStacked;
    const percentage = (profitLoss / totalStacked) * 100;

    return {
      difference: profitLoss,
      percentageChange: percentage,
      currentValue: totalBalance,
      firstDate: performanceData[0]?.timestamp,
      lastDate: performanceData[performanceData.length - 1]?.timestamp,
    };
  }, [performanceData, autoStackStats]);

  const tooltipConfig = useMemo(
    () => ({
      enabled: true,
      mode: "index" as const,
      intersect: false,
      backgroundColor: "#57249C",
      titleColor: "#ffffff",
      bodyColor: "#ffffff",
      borderColor: "#57249C",
      borderWidth: 1,
      cornerRadius: 8,
      displayColors: false,
      callbacks: {
        title: (context: any) => {
          const dataIndex = context[0].dataIndex;
          const date = performanceData[dataIndex]?.timestamp;
          return dayjs(date).format("DD/MM/YYYY");
        },
        label: (context: any) => `R${context.parsed.y.toFixed(2)}`,
      },
    }),
    [performanceData]
  );

  const getScalesConfig = useCallback(
    () => ({
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: "hsl(var(--text-muted))",
          font: { size: 12 },
          autoskip: false,
          maxTicksLimit: 8,
          callback: (index: number) => {
            const dataItem = performanceData.at(index);
            if (!dataItem) {
              return "";
            }
            const date = dayjs(dataItem.timestamp);
            return date.format(dateLabelFormat);
          },
        },
      },
      y: {
        display: true,
        position: "left" as const,
        min: 0,
        grid: {
          display: true,
          color: "rgba(0, 0, 0, 0.05)",
          drawBorder: false,
        },
        border: { display: false },
        ticks: {
          color: "hsl(var(--text-muted))",
          font: { size: 12 },
          callback: (value: any) => {
            const numValue = Number(value);

            if (numValue < 100 && numValue !== Math.floor(numValue)) return "";

            if (numValue >= 1000000000)
              return `R ${(numValue / 1000000000).toFixed(1)}B`;
            if (numValue >= 1000000)
              return `R ${(numValue / 1000000).toFixed(1)}M`;
            if (numValue >= 1000) return `R ${(numValue / 1000).toFixed(0)}k`;
            return `R ${Math.round(numValue)}`;
          },
          maxTicksLimit: 5,
        },
      },
    }),
    [dateLabelFormat, performanceData]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: tooltipConfig,
      },
      scales: getScalesConfig(),
      interaction: {
        intersect: false,
        mode: "index" as const,
      },
    }),
    [tooltipConfig, getScalesConfig]
  );

  if (isLoading) {
    return <AutoStackPerformanceChartSkeleton />;
  }

  return (
    <div className="bg-white rounded-lg p-6">
      {singleAutoStack && singleAutoStackMetrics ? (
        <div className="space-y-6">
          <NavigationHeader
            title={`${singleAutoStackMetrics.stackName} Vault Performance`}
            onBack={() => navigate("/autostack")}
            rightElement={
              <Button
                variant="ghost"
                size="sm"
                className="text-xs md:text-sm border border-bg-section rounded-full px-4 text-text-primary"
                onClick={() =>
                  navigate(`/autostack/edit/${singleAutoStackMetrics.stackId}`)
                }
              >
                Edit Stack
              </Button>
            }
          />

          <div className="flex flex-col">
            <div className="flex items-center gap-1 mb-2">
              <img
                src={singleAutoStackMetrics.image}
                alt={singleAutoStackMetrics.assetName}
                className="w-6 h-6"
              />
              <Typography variant="span" className="text-text-secondary">
                {singleAutoStackMetrics.assetName}
              </Typography>
            </div>
            <PerformanceDisplay
              currentValue={singleAutoStackMetrics.currentValue}
              difference={singleAutoStackMetrics.performanceDifference}
              percentage={singleAutoStackMetrics.performancePercentage}
              currencyFormatter={currencyFormatter}
              showLabel={true}
              labelText="Current Value"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 pb-6">
            <MetricDisplay
              label="Total stacked"
              value={currencyFormatter.format(
                singleAutoStackMetrics.totalStacked,
                "fiat",
                "ZAR"
              )}
            />
            <MetricDisplay
              label="Total accumulated"
              value={currencyFormatter.format(
                singleAutoStackMetrics.assetBalance,
                "crypto",
                singleAutoStackMetrics.assetName,
                { showSymbol: true }
              )}
            />
            <MetricDisplay
              label="Avg buy price"
              value={currencyFormatter.format(
                singleAutoStackMetrics.averageBuyPrice,
                "fiat",
                "ZAR"
              )}
            />
          </div>
        </div>
      ) : overallAutoStack ? (
        <div className="space-y-2">
          <NavigationHeader
            title="Performance Overview"
            onBack={() => navigate("/autostack")}
            rightElement={
              <div className="flex flex-wrap md:flex-nowrap bg-gray-100 hover:bg-gray-50 py-2 px-3 rounded-full items-center justify-center md:justify-start gap-2">
                <Typography
                  variant="span"
                  className="text-xs md:text-sm text-text-primary"
                >
                  Total stacked
                </Typography>
                <Typography
                  variant="span"
                  className="text-xs md:text-sm text-brand-primary font-semibold"
                >
                  {currencyFormatter.format(
                    autoStackStats?.totalStacked,
                    "fiat",
                    "ZAR"
                  )}
                </Typography>
              </div>
            }
          />
          {performanceStats && (
            <div className="flex items-center gap-2 pb-4">
              <Typography
                variant="h2"
                className="text-lg md:text-3xl text-brand-primary font-bold"
              >
                {currencyFormatter.format(
                  performanceStats.currentValue,
                  "fiat",
                  "ZAR"
                )}
              </Typography>
              <div className="flex items-center gap-1">
                <Typography
                  variant="span"
                  className="text-emerald-500 text-xs md:text-sm font-semibold"
                >
                  +{" "}
                  {currencyFormatter.format(
                    performanceStats.difference,
                    "fiat",
                    "ZAR"
                  )}
                </Typography>
                <Typography
                  variant="span"
                  className="text-emerald-500 text-xs md:text-sm font-semibold"
                >
                  ({performanceStats.percentageChange.toFixed(0)}%)
                </Typography>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex justify-between items-center">
          <div className="flex flex-col mb-6">
            <Typography
              variant="h2"
              className="text-lg md:text-2xl font-semibold text-brand-primary"
            >
              Performance
            </Typography>
            <Button
              onClick={() => navigate("/autostack/overall")}
              className="text-sm md:text-base text-accent-pink font-semibold transition-colors bg-transparent hover:bg-transparent w-fit p-0"
              onMouseEnter={(e) => {
                (e.target as HTMLButtonElement).style.color =
                  "hsl(var(--accent-magenta))";
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLButtonElement).style.color =
                  "hsl(var(--accent-pink))";
              }}
            >
              View more
            </Button>
          </div>

          {performanceStats && (
            <div className="mb-6">
              <div className="text-right">
                <Typography
                  variant="p"
                  className="text-xs text-text-muted mb-1"
                >
                  Total balance
                </Typography>
                <div className="flex flex-col items-end">
                  <Typography
                    variant="h2"
                    className="text-lg md:text-3xl text-brand-primary font-bold mb-2"
                  >
                    {currencyFormatter.format(
                      autoStackStats?.totalBalance,
                      "fiat",
                      "ZAR"
                    )}
                  </Typography>
                  <div className="flex items-center justify-end gap-1">
                    <Typography
                      variant="span"
                      className="text-emerald-500 text-xs md:text-sm font-semibold"
                    >
                      +{" "}
                      {currencyFormatter.format(
                        performanceStats.difference,
                        "fiat",
                        "ZAR"
                      )}
                    </Typography>
                    <Typography
                      variant="span"
                      className="text-emerald-500 text-sm font-semibold"
                    >
                      ({performanceStats.percentageChange.toFixed(0)}%)
                    </Typography>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      <div className={chartHeight}>
        <Line data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};

export default AutoStackPerformanceChart;
